(ns manage.content-configuration-page.rule.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [common.ui.entity :refer [query-param->config]]
            [common.ui.list :as ui.list]
            [common.ui.store :as store]
            [common.ui.tabs :refer [tabs]]
            [common.ui.validation.view :as validation]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.form-field :refer [form-field]]
            [manage.content-configuration-page.placeholder-expression.view
             :as placeholder-expression]
            [manage.content-configuration-page.predicate.view :as predicate]
            [manage.content-configuration-page.rule.db :refer [pull-pattern-rule]]
            [manage.content-configuration-page.view.list :as lv]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reagent.ratom :as ratom]
            [uix.core :refer [$]]))

(defn assignments [r]
  (let [published? @(rf/subscribe [:resource.content-function-version/active-version? "published"])
        options @(rf/subscribe [:content-configuration/all-rule-assignment-options])
        assigned-phe-p @(rf/subscribe [:manage.content-configuration-page.rule.db/assigned-placeholders r])
        assigned (map last (sort assigned-phe-p))
        all (map :value options)
        all-assigned? (= (set all) (set assigned))
        assign (fn [placeholders]
                 (rf/dispatch [:rule/sync-assignments r assigned-phe-p placeholders]))]
    ($ Box {:p 3 :data-component "assign-placeholders"}
       ($ form-field
          {:label ($ Stack {:direction "row"
                            :justifyContent "space-between"
                            :alignItems "center"}
                     ($ Typography {:component "label" :fontWeight "600"}
                        "Assign placeholder")
                     ($ Button {:onClick #(assign all)
                                :variant "text"
                                :sx #js {:py 0}
                                :disabled (or published? all-assigned?)
                                :startIcon ($ Icon "add")}
                        "Assign all"))}
          (r/as-element
           [dropdown {:options options
                      :multi? true
                      :clearable? true
                      :disabled? published?
                      :value assigned
                      :on-change assign}])))))

(declare list-cfg)

(rf/reg-sub-raw :rule/tab-validation
  (fn [_ [_ ref attr]]
    (ratom/reaction
     (let [value (attr @(store/entity ref pull-pattern-rule))
           validation-results @(rf/subscribe [:validation/results-from-value attr value])]
       (group-by :stage validation-results)))))

(defn- label-with-validation-results
  [{attr :list/items :entity/keys [ref label] :as cfg}]
  (assoc cfg
         :entity/label-element
         ($ Stack {:direction "row" :spacing 0.5}
            ($ :p label)
            ($ validation/view-results {:stack-props {:data-component :tab-validation}
                                        :render-type :dot
                                        :validation-results-sub [:rule/tab-validation ref attr]}))))

(defn- list-assignments-cfg [rule]
  {:entity/ref rule
   :entity/label "Content"
   :list/empty-msg
   ($ ui.list/empty-msg {:title "Assign placeholders to this rule"}
      ($ :p
         "Due to the cascading nature of rules, each rule can
          overwrite different placeholders. Here you can define which
          placeholder this rule should overwrite, by assigning one or
          multiple placeholders above.")
      ($ :p
         "When there are no placeholders to select, please make sure to "
         ($ ui.list/empty-msg-link {:menu :placeholders} "add placeholders")
         " to your adset in advance."))
   :list/content-before (fn [_ _] [assignments (:eid rule)])
   :list/items :rule/assignment
   :list/sort-by placeholder-expression/visualization-order
   :list/->item-cfg placeholder-expression/entity-view-cfg})

(defn entity-view-cfg [{r :eid :as rule}]
  {:entity/ref rule
   :entity/label :rule/name
   :validation/schema :rule
   :validation/render-type :summary
   :validation/pull-pattern pull-pattern-rule
   :entity/context-menu [{:label "Duplicate" :icon "file_copy"
                          :on-click (fn [_]
                                      (rf/dispatch [:rule/duplicate r]))}
                         {:label "Delete" :icon "delete"
                          :on-click (fn [_]
                                      (rf/dispatch [:ui.store/delete r
                                                    :content-configuration/rule
                                                    :rule/execution-order])
                                      (rf/dispatch [:side-menu/open :rules]))}]
   :entity/query-param [:rule/query-params rule]

   :entity/content
   ($ tabs {:key :rule
            :tabs {:conditions (lv/as-tab (label-with-validation-results (predicate/list-cfg-predicate rule)))
                   :assignments (lv/as-tab (label-with-validation-results (list-assignments-cfg rule)))}
            :bind-active-tab-to-query-param true})})

(defn- list-cfg-common [cc]
  {:entity/ref cc
   :entity/icon "format_list_numbered"
   :entity/label "Rules"
   :validation/schema :content-configuration
   :validation/pull-pattern [{:content-configuration/rule pull-pattern-rule}]
   :list/items :content-configuration/rule
   :list/sort-by :rule/execution-order
   :list/->item-cfg entity-view-cfg
   :list/item-labels-editable? false
   :list/highlighted true})

(defn list-cfg [cc]
  (lv/as-entity
   (assoc
    (list-cfg-common cc)
    :list/empty-msg ($ ui.list/empty-msg {:title "Create your rules"}
                       ($ :p
                          "To add content variants to your configuration, you can
                           start creating rules here.")
                       ($ :p
                          "Rules consist of the content per placeholder that you
                           want to overwrite, and the data-conditions that are
                           used to trigger the rule."))
    :list/add-item-cfg
    {:add-item/label "Add rule"
     :validation/schema :rule/name
     :add-item/new-value [:add-item/suggest-name :rule/name "rule"]
     :add-item/input-props {:data-testid "rule-name-input"}
     :add-item/add-fn #(rf/dispatch [:rule/create %])}
    :list/reorderable? true)))

(defn read-only-list-cfg [cc]
  (lv/as-entity
   (assoc
    (list-cfg-common cc)
    :entity/read-only? true
    :list/empty-msg ($ ui.list/empty-msg {:title "Create your rules"}
                       ($ :p
                          "This overview is empty at the moment. "
                          ($ ui.list/empty-msg-link
                             {:data-testid "Create rule"
                              :menu :rules}
                             "Create rules")
                          " to configure your content dynamically.")))))

(defmethod query-param->config :rule
  [{rule-name :name}]
  (some-> @(rf/subscribe [:ui.store/lookup :rule/name rule-name])
          first store/->DataScriptDBEntity entity-view-cfg))
